function interpolate(template, vars = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => (
    vars[key] == null ? `{${key}}` : String(vars[key])
  ));
}

function lookup(source, path) {
  return path.split('.').reduce((current, part) => (
    current && typeof current === 'object' ? current[part] : undefined
  ), source);
}

export const messages = {
  en: {
    appName: 'PACILO',
    appTagline: 'Discover your professional personality',
    language: { ar: 'العربية', en: 'English' },
    meta: {
      welcome: 'PACILO — Discover your professional personality',
      auth: 'Sign in to PACILO',
      verify: 'Verify your phone',
      home: 'Home',
      assessment: 'Professional assessment',
      result: 'Your professional result',
      consultations: 'Career consultations',
      notifications: 'Notifications',
      support: 'Support',
      profile: 'Profile',
      legal: 'Legal',
      delete: 'Delete account',
      companyPlans: 'Company assessment plans',
      companyPlan: 'Company plan',
      companyOnboarding: 'Company onboarding',
      companyDashboard: 'Company dashboard',
      companyPurchases: 'Company purchases',
      companyPurchase: 'Purchase details',
      companyPurchaseStatus: 'Purchase status',
      companyCredits: 'Company credits',
      companyCodes: 'Assessment codes',
      companyParticipants: 'Company participants',
      companyParticipantResult: 'Participant result',
      companySettings: 'Company settings',
      companyCode: 'Company assessment code',
    },
    actions: {
      continue: 'Continue',
      start: 'Get started',
      login: 'Sign in',
      logout: 'Log out',
      next: 'Next',
      previous: 'Previous',
      back: 'Back',
      retry: 'Try again',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      close: 'Close',
      submit: 'Submit',
      refresh: 'Refresh status',
      resend: 'Resend code',
      changePhone: 'Change phone number',
      markAllRead: 'Mark all as read',
      create: 'Create',
      view: 'View',
      open: 'Open',
      accept: 'I accept',
      delete: 'Delete account',
      request: 'Request',
      withdraw: 'Withdraw consent',
      startAssessment: 'Start assessment',
      resumeAssessment: 'Resume assessment',
      viewResult: 'View result',
      viewHistory: 'Result history',
      requestConsultation: 'Request a consultation',
      viewConsultation: 'View consultation',
      sendTicket: 'Send ticket',
    },
    layout: {
      skipToContent: 'Skip to main content',
      navigation: 'Main navigation',
      desktopNavigation: 'Desktop navigation',
      mobileNavigation: 'Mobile navigation',
      publicNavigation: 'Public navigation',
      dashboardNavigation: 'Dashboard navigation',
      footerNavigation: 'Footer navigation',
      language: 'Language',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      menu: 'Menu',
      footerCopy: 'PACILO professional personality assessment.',
    },
    nav: {
      home: 'Home',
      assessment: 'Assessment',
      results: 'Results',
      consultations: 'Consultations',
      notifications: 'Notifications',
      profile: 'Profile',
      support: 'Support',
      terms: 'Terms',
      privacy: 'Privacy',
      companyPlans: 'Company plans',
      company: 'Company',
    },
    welcome: {
      kicker: 'Professional personality assessment',
      title: 'Understand how you work — and where you can grow',
      body: 'PACILO helps you explore your professional patterns, review a personalised result, and talk with a career specialist when you need guidance.',
      point1Title: 'Answer at your pace',
      point1Body: 'Answer the questionnaire at your own pace. Your answers are saved so you can resume later.',
      point2Title: 'A result you can use',
      point2Body: 'See your professional patterns, a personal summary, and suggested paths — scored only by PACILO.',
      point3Title: 'Guidance when you need it',
      point3Body: 'Request a career consultation, follow notifications, and reach support from one place.',
      cta: 'Start with your phone number',
      legalHint: 'By continuing you can review the current Terms and Privacy before creating an account.',
      featuresTitle: 'What PACILO offers',
      howTitle: 'How the assessment works',
      howBody: 'Sign in, answer the published questions at your own pace, then review a server-scored result you can discuss with a specialist.',
      consultTitle: 'Need a specialist conversation?',
      consultBody: 'After you have a completed result, you can request a career consultation from your PACILO account. PACILO assigns an appropriate career specialist after review. This website does not charge for consultations.',
    },
    auth: {
      title: 'Sign in or create an account',
      subtitle: 'We will send a one-time code to your Saudi mobile number.',
      phone: 'Mobile number',
      phoneHint: 'Use a Saudi number such as 05XXXXXXXX.',
      channel: 'Delivery channel',
      sms: 'SMS',
      whatsapp: 'WhatsApp',
      sendCode: 'Send code',
      verifyTitle: 'Enter the verification code',
      verifySubtitle: 'We sent a 6-digit code to {phone}.',
      code: 'Verification code',
      expiresIn: 'Code expires in {seconds}s',
      resendIn: 'You can resend in {seconds}s',
      remainingAttempts: '{count} attempts remaining',
      codeExpired: 'This code has expired. Request a new one.',
      invalidCode: 'That verification code is not valid.',
      changePhone: 'Use a different number',
      sessionExpired: 'Your session expired. Please sign in again.',
      bootstrapping: 'Restoring your session…',
      temporarilyUnavailable: 'PACILO is temporarily unavailable.',
      temporarilyUnavailableBody: 'Your sign-in is still saved on this device. Try again without signing in again.',
      storageUnavailable: 'This browser could not save your sign-in. Allow site data for PACILO, then try again.',
      originMismatch: 'Use {origin} for local PACILO development. This address is a different browser origin.',
      fullName: 'Full name',
      fullNameHint: 'Required for new PACILO accounts.',
      newAccount: 'New account details',
      acceptTerms: 'I have read and accept the Terms of Use',
      acceptPrivacy: 'I have read and accept the Privacy Policy',
      codeNoLongerValid: 'This verification code has expired or is no longer valid. Request a new code to continue.',
      requestNewCode: 'Request a new code',
      otpDeliveryUnavailable: 'Verification messages are temporarily unavailable. Stay on this page and try again when you choose.',
    },
    profileComplete: {
      title: 'Complete your profile',
      body: 'Add your professional details so PACILO can personalise your experience.',
      submit: 'Save and continue',
      legalLoading: 'Loading required documents…',
      legalFailed: 'Required legal documents could not be loaded. Try again before continuing.',
    },
    legal: {
      acceptanceTitle: 'Review and accept',
      acceptanceBody: 'A current legal document needs your acceptance before you continue.',
      version: 'Version {version}',
      effective: 'Effective {date}',
      missing: 'A current published document is not available yet.',
      terms: 'Terms of Use',
      privacy: 'Privacy Policy',
      acceptAll: 'Accept and continue',
      required: 'Acceptance is required to use PACILO.',
      read: 'Read',
      explicitHint: 'Read each document, then check the box. PACILO never records acceptance during login.',
    },
    home: {
      greeting: 'Welcome back, {name}',
      greetingGuest: 'Welcome to PACILO',
      assessmentReady: 'Your professional assessment is ready.',
      assessmentUnavailable: 'No published assessment is available yet.',
      inProgress: 'You have an assessment in progress ({answered} of {total} answered).',
      latestResult: 'Latest result',
      noResult: 'Complete the assessment to see your professional profile.',
      consultation: 'Career consultation',
      noConsultation: 'Request a consultation after you have a completed result.',
      unread: '{count} unread notifications',
    },
    assessment: {
      introTitle: 'Professional personality assessment',
      introBody: 'This assessment explores how you prefer to work. Answer honestly. There are no right or wrong answers.',
      howTitle: 'How it works',
      howBody: 'Questions are shown in small groups in the order Laravel returns. You can move back to review previous answers. Your answers are saved as you go.',
      privacyTitle: 'Your result',
      privacyBody: 'PACILO calculates your professional patterns on the server. The website never scores answers or rearranges the official result order.',
      section: 'Section {current} of {total}',
      progress: 'Question {current} of {total}',
      questionRange: 'Questions {start}–{end} of {total}',
      groupProgress: 'Group {current} of {total}',
      invalid: 'This assessment could not be loaded because the question list is invalid.',
      chooseAnswer: 'Choose one answer',
      saveFailed: 'Your answer was not saved. Try again before continuing.',
      submitTitle: 'Submit your assessment?',
      submitBody: 'You will not be able to change these answers after submission. PACILO will prepare your result.',
      submitting: 'Submitting your assessment…',
      empty: 'This assessment has no active questions yet.',
      unavailable: 'No published assessment is available.',
      completed: 'This attempt is already completed.',
      stale: 'This attempt is no longer valid. Start or resume the current assessment.',
      generating: 'Your result is being prepared.',
      questionCount: '{count} active questions',
      sectionCount: '{count} sections',
      personalTitle: 'Your personal assessment',
      personalBody: 'Start or resume your own PACILO professional assessment. This is separate from a company-sponsored code.',
      sponsoredBadge: 'Assessment provided by {companyName}',
      companyCode: {
        title: 'Do you have an assessment code from a company?',
        description: 'Enter the code your company provided to start a company-sponsored assessment.',
        codeLabel: 'Assessment code',
        validate: 'Verify code',
        companyLabel: 'Provided by {companyName}',
        startAttempt: 'Use code and start assessment',
        resumeAttempt: 'Use code and resume assessment',
        sponsorExplain: 'This assessment is sponsored by the company named above. After you finish, the company will receive your full name and complete user-facing assessment result.',
        consent: 'I agree to share my full name and complete professional assessment result with the sponsoring company named above.',
        startHint: 'This code will start a new sponsored assessment attempt.',
        resumeHint: 'You already claimed this code. You will resume your existing sponsored attempt.',
        expiresAt: 'Valid until {date}',
        validating: 'Verifying code…',
        validated: 'Code verified for {company}.',
        redeeming: 'Starting assessment…',
        cancel: 'Clear and enter another code',
        unavailable: 'This assessment code is not available.',
        inProgressConflict: 'You already have an assessment in progress that cannot be replaced. Finish or resume it before using this code.',
      },
    },
    results: {
      title: 'Your professional result',
      historyTitle: 'Result history',
      latest: 'Latest',
      profileCode: 'Profile code',
      topPattern: 'Top pattern',
      tied: 'Tied',
      patterns: 'Professional patterns',
      personalSummary: 'Personal summary',
      empty: 'You do not have a completed result yet.',
      snapshot: 'Historical snapshot',
      deletedItem: 'This item is no longer in the live catalogue. The saved snapshot is shown.',
      sponsoredBy: 'Sponsored by {companyName}',
      types: {
        key_strength: 'Key strength',
        professional_growth_challenge_opportunity: 'Opportunity for professional growth',
        suggested_career_path: 'Suggested career path',
        skill_worth_developing: 'Skill worth developing',
      },
      advice: 'Advice',
    },
    consultations: {
      title: 'Career consultations',
      newTitle: 'Request a consultation',
      empty: 'You have not requested a consultation yet.',
      needsResult: 'Complete an assessment first. A consultation request needs a completed result.',
      activeHint: 'You already have a consultation in progress. PACILO will assign a specialist after review.',
      assignHint: 'PACILO assigns an appropriate career specialist after reviewing your request. You do not choose a specialist here.',
      goal: 'Consultation goal',
      question: 'Your question or notes',
      period: 'Preferred period',
      method: 'Communication method',
      methodVideo: 'Video',
      attempt: 'Linked assessment result',
      consent: 'I consent to sharing my report with the assigned specialist',
      consentHint: 'You can withdraw future report access later. The consultation remains.',
      consentYes: 'Share the report with the assigned specialist',
      consentNo: 'Do not share the report',
      review: 'Review your request',
      success: 'Consultation requested',
      reference: 'Reference {reference}',
      referenceLabel: 'Reference',
      specialist: 'Assigned specialist',
      specialistPhoto: 'Portrait of {name}',
      specialistPhotoFallback: 'Career specialist portrait',
      specialistUnavailable: 'Specialist no longer available',
      scheduled: 'Scheduled',
      meeting: 'Meeting link',
      timezone: 'Time zone',
      reason: 'Reason',
      cancelTitle: 'Cancel this consultation?',
      cancelBody: 'You can cancel only while the request is pending review.',
      withdrawTitle: 'Withdraw report consent?',
      withdrawBody: 'The specialist will not receive future access to your report. The consultation is not deleted.',
      noPayment: 'PACILO consultations on this website do not include payment or pricing.',
      statuses: {
        pending_review: 'Pending review',
        matched: 'Matched',
        scheduled: 'Scheduled',
        completed: 'Completed',
        cancelled: 'Cancelled',
        rejected: 'Rejected',
      },
    },
    notifications: {
      title: 'Notifications',
      empty: 'You have no notifications yet.',
      unread: 'Unread',
      read: 'Read',
    },
    support: {
      title: 'Support',
      newTitle: 'New support ticket',
      categories: 'How can we help?',
      tickets: 'Your tickets',
      emptyCategories: 'No support categories are available right now.',
      emptyTickets: 'You have not sent a support ticket yet.',
      category: 'Category',
      message: 'Message',
      counter: '{count} / {max}',
      success: 'Ticket sent',
      reference: 'Reference {reference}',
      response: 'Administrative response',
      noResponse: 'No response yet.',
      statuses: {
        open: 'Open',
        in_review: 'In review',
        resolved: 'Resolved',
        closed: 'Closed',
      },
    },
    profile: {
      title: 'Profile and settings',
      details: 'Profile details',
      name: 'Full name',
      fullName: 'Full name',
      phone: 'Phone number',
      locale: 'Preferred language',
      ageRange: 'Age range',
      educationLevel: 'Education level',
      isEmployed: 'Are you currently employed?',
      yes: 'Yes',
      no: 'No',
      workExperience: 'Work experience',
      experienceField: 'Field of experience',
      skills: 'Skills',
      skillsHint: 'Add a skill and press Enter or comma. Duplicates are ignored.',
      skillsAdd: 'Add skill',
      skillsPlaceholder: 'Type a skill',
      removeSkill: 'Remove {skill}',
      skillAdded: 'Added {skill}',
      skillRemoved: 'Removed {skill}',
      country: 'Country',
      selectPlaceholder: 'Select an option',
      optionsLoading: 'Loading profile options…',
      optionsFailed: 'Profile options could not be loaded. Try again before continuing.',
      required: 'This field is required.',
      invalid: 'This value is not valid.',
      saved: 'Your profile was saved.',
      links: 'Account links',
      danger: 'Danger zone',
    },
    deletion: {
      title: 'Delete your account',
      body: 'This archives and anonymises your PACILO account. Operational records such as attempts, consultations, and support tickets may remain attached to an anonymised identifier. This is not a claim of complete physical erasure.',
      confirmExplain: 'Request a verification code, then type the English word DELETE exactly as required by the server. This confirmation word is not translated.',
      request: 'Send deletion code',
      destination: 'A code will be sent to your registered phone number.',
      expires: 'The code expires in {seconds}s',
      code: 'Verification code',
      confirmation: 'Type DELETE to confirm',
      confirmationValue: 'DELETE',
      success: 'Your account has been deleted.',
    },
    errors: {
      unexpected: 'Something went wrong. Please try again.',
      network: 'You appear to be offline or the service is unavailable.',
      throttled: 'Too many attempts. Please wait and try again.',
      throttledWait: 'Too many attempts. Try again in {seconds}s.',
      validation: 'Please check the highlighted fields.',
      forbidden: 'You do not have access to this page.',
      notFound: 'This page could not be found.',
      conflict: 'This action is not available in the current state.',
      server: 'PACILO is temporarily unavailable. Please try again later.',
      envelope: 'The server returned an unexpected response.',
    },
    empty: {
      generic: 'Nothing to show yet.',
    },
    status: {
      loading: 'Loading',
    },
    confirm: {
      title: 'Please confirm',
    },
    notFound: {
      title: 'Page not found',
      body: 'The page you are looking for does not exist.',
      home: 'Back to PACILO',
    },
    forbidden: {
      title: 'Access restricted',
      body: 'This account cannot use PACILO right now.',
    },
    conflict: {
      title: 'This action is not available',
      body: 'The current state does not allow this request. Refresh and try a valid next step.',
    },
    throttled: {
      title: 'Please wait',
      body: 'Too many attempts were made. Wait a moment, then try again.',
    },
    company: {
      nav: {
        label: 'Company portal navigation',
        dashboard: 'Dashboard',
        plans: 'Plans',
        purchases: 'Purchases',
        credits: 'Credits',
        codes: 'Codes',
        participants: 'Participants',
        settings: 'Settings',
      },
      plans: {
        kicker: 'For organisations',
        title: 'Company assessment plans',
        body: 'Purchase assessment credits for your team, generate secure access codes, and review completed participant results.',
        empty: 'No company plans are available right now.',
        featured: 'Featured',
        credits: '{count} assessments',
        validity: 'Valid for {days} days after activation',
        view: 'View plan',
        detailsTitle: 'Plan details',
        terms: 'Plan terms',
        companyExplain: 'After payment is confirmed by PACILO, your company receives credits to generate one-time assessment codes for participants.',
        purchase: 'Purchase plan',
      },
      onboarding: {
        kicker: 'Company setup',
        title: 'Create your company profile',
        body: 'Register your organisation before purchasing assessment plans or managing team access.',
        legalName: 'Legal company name',
        displayName: 'Display name',
        displayNameHint: 'Shown to participants when they redeem a code.',
        registrationNumber: 'Registration number',
        contactEmail: 'Contact email',
        contactPhone: 'Contact phone',
        contactAddress: 'Contact address',
        submit: 'Create company',
      },
      dashboard: {
        kicker: 'Company portal',
        body: 'Track credits, codes, and completed participant assessments.',
        activePlans: 'Active plans',
        totalCredits: 'Total credits',
        availableCredits: 'Available credits',
        activeCodes: 'Active codes',
        claimedCodes: 'Claimed codes',
        completedAssessments: 'Completed assessments',
        recentParticipants: 'Recent participants',
      },
      purchases: {
        title: 'Purchase history',
        body: 'Review plan purchases and payment status.',
        empty: 'No purchases yet.',
        buyPlan: 'Browse plans',
      },
      purchase: {
        processing: 'Starting checkout…',
        amount: 'Amount',
        credits: 'Included assessments',
        created: 'Created',
        paidAt: 'Paid at',
        reference: 'Payment reference',
        plan: 'Plan',
        pendingTitle: 'Payment pending',
        pendingBody: 'Your purchase is waiting for payment confirmation. Credits appear only after PACILO confirms payment.',
        checkStatus: 'Check payment status',
        viewDetails: 'View purchase details',
        goToCodes: 'Generate codes',
        backToPurchases: 'Back to purchases',
        statuses: {
          pending_payment: 'Pending payment',
          paid: 'Paid',
          failed: 'Failed',
          cancelled: 'Cancelled',
          refunded: 'Refunded',
        },
        statusPage: {
          pendingTitle: 'Payment is still pending',
          pendingBody: 'We are checking the server purchase status. Credits are not available until payment is confirmed.',
          successTitle: 'Payment confirmed',
          successBody: 'Your purchase is active. You can now generate assessment codes.',
          failedTitle: 'Payment was not completed',
          failedBody: 'This purchase failed before payment was confirmed. Start a new purchase if you still need credits.',
          cancelledTitle: 'Purchase cancelled',
          cancelledBody: 'This purchase was cancelled before payment was confirmed.',
          refundedTitle: 'Purchase refunded',
          refundedBody: 'This purchase was refunded. Credits from this purchase are no longer available.',
        },
      },
      credits: {
        title: 'Credits',
        body: 'Credit balances come from the PACILO server and update after purchases, code generation, and redemption.',
        total: 'Total credits',
        available: 'Available credits',
        reserved: 'Reserved credits',
        consumed: 'Consumed credits',
        entitlements: 'Active entitlements',
        empty: 'No active entitlements yet.',
        availableCount: '{count} available',
      },
      codes: {
        title: 'Assessment codes',
        body: 'Generate secure one-time codes for participants. Share codes privately.',
        generate: 'Generate codes',
        generateTitle: 'Generate assessment codes',
        generatedTitle: 'Codes generated',
        entitlement: 'Entitlement',
        quantity: 'Number of codes',
        batchLabel: 'Batch label (optional)',
        remainingCredits: '{count} credits available for this entitlement',
        search: 'Search codes or participants',
        allStatuses: 'All statuses',
        empty: 'No codes yet.',
        copy: 'Copy',
        copyAll: 'Copy all',
        copySuccess: 'Copied to clipboard',
        downloadCsv: 'Download CSV',
        securityWarning: 'Share these codes securely. Anyone with a code can start a sponsored assessment.',
        revoke: 'Revoke',
        revokeTitle: 'Revoke unused code?',
        revokeBody: 'This code can no longer be redeemed. Credits return only when the server allows it.',
        revokeConfirm: 'Revoke code',
        statuses: {
          active: 'Active',
          claimed: 'Claimed',
          completed: 'Completed',
          revoked: 'Revoked',
          expired: 'Expired',
        },
      },
      participants: {
        title: 'Participants',
        body: 'Participants who redeemed a company code. Results appear only after completion.',
        empty: 'No participants yet.',
      },
      participantResult: {
        badge: 'Company-sponsored participant result',
        participant: 'Participant: {name}',
      },
      settings: {
        title: 'Company settings',
        body: 'Update company profile details and review authorized members.',
        members: 'Authorized members',
        saved: 'Company profile updated.',
      },
      codeEntry: {
        kicker: 'Sponsored assessment',
        title: 'Use a company assessment code',
        body: 'Enter the code provided by your organisation. Codes are never stored in the browser address bar.',
        codeLabel: 'Assessment code',
        codeHint: 'Format like PACILO-XXXX-XXXX-XXXX',
        validate: 'Continue',
        sponsorExplain: 'The sponsoring company will receive your full name and complete user-facing assessment result after you finish.',
        consentFallback: 'I agree that the sponsoring company will receive my full name and complete assessment result.',
        redeem: 'Redeem code and start',
      },
    },
  },
  ar: {
    appName: 'باكيلو',
    appTagline: 'اكتشف شخصيتك المهنية',
    language: { ar: 'العربية', en: 'English' },
    meta: {
      welcome: 'باكيلو — اكتشف شخصيتك المهنية',
      auth: 'تسجيل الدخول إلى باكيلو',
      verify: 'تأكيد رقم الجوال',
      home: 'الرئيسية',
      assessment: 'التقييم المهني',
      result: 'نتيجتك المهنية',
      consultations: 'الاستشارات المهنية',
      notifications: 'الإشعارات',
      support: 'الدعم',
      profile: 'الملف الشخصي',
      legal: 'المستندات النظامية',
      delete: 'حذف الحساب',
      companyPlans: 'باقات تقييم الشركات',
      companyPlan: 'باقة الشركة',
      companyOnboarding: 'إعداد الشركة',
      companyDashboard: 'لوحة الشركة',
      companyPurchases: 'مشتريات الشركة',
      companyPurchase: 'تفاصيل الشراء',
      companyPurchaseStatus: 'حالة الدفع',
      companyCredits: 'رصيد الشركة',
      companyCodes: 'رموز التقييم',
      companyParticipants: 'مشاركو الشركة',
      companyParticipantResult: 'نتيجة المشارك',
      companySettings: 'إعدادات الشركة',
      companyCode: 'رمز تقييم الشركة',
    },
    actions: {
      continue: 'متابعة',
      start: 'ابدأ',
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      next: 'التالي',
      previous: 'السابق',
      back: 'رجوع',
      retry: 'إعادة المحاولة',
      cancel: 'إلغاء',
      confirm: 'تأكيد',
      save: 'حفظ',
      close: 'إغلاق',
      submit: 'إرسال',
      refresh: 'تحديث الحالة',
      resend: 'إعادة إرسال الرمز',
      changePhone: 'تغيير رقم الجوال',
      markAllRead: 'تعليم الكل كمقروء',
      create: 'إنشاء',
      view: 'عرض',
      open: 'فتح',
      accept: 'أوافق',
      delete: 'حذف الحساب',
      request: 'طلب',
      withdraw: 'سحب الموافقة',
      startAssessment: 'بدء التقييم',
      resumeAssessment: 'استكمال التقييم',
      viewResult: 'عرض النتيجة',
      viewHistory: 'سجل النتائج',
      requestConsultation: 'طلب استشارة',
      viewConsultation: 'عرض الاستشارة',
      sendTicket: 'إرسال التذكرة',
    },
    layout: {
      skipToContent: 'تخطي إلى المحتوى الرئيسي',
      navigation: 'التنقل الرئيسي',
      desktopNavigation: 'تنقل سطح المكتب',
      mobileNavigation: 'تنقل الجوال',
      publicNavigation: 'تنقل الصفحات العامة',
      dashboardNavigation: 'تنقل لوحة الحساب',
      footerNavigation: 'تنقل التذييل',
      language: 'اللغة',
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      menu: 'القائمة',
      footerCopy: 'باكيلو لتقييم الشخصية المهنية.',
    },
    nav: {
      home: 'الرئيسية',
      assessment: 'التقييم',
      results: 'النتائج',
      consultations: 'الاستشارات',
      notifications: 'الإشعارات',
      profile: 'حسابي',
      support: 'الدعم',
      terms: 'الشروط',
      privacy: 'الخصوصية',
      companyPlans: 'باقات الشركات',
      company: 'الشركة',
    },
    welcome: {
      kicker: 'تقييم الشخصية المهنية',
      title: 'افهم أسلوب عملك، واكتشف فرص نموك',
      body: 'يساعدك باكيلو على استكشاف أنماطك المهنية، ومراجعة نتيجة مخصصة، والتواصل مع مختص مهني عند الحاجة.',
      point1Title: 'أجب وفق وتيرتك',
      point1Body: 'أجب عن الأسئلة وفق وتيرتك، ويتم حفظ إجاباتك لتتمكن من الاستكمال لاحقاً.',
      point2Title: 'نتيجة يمكنك الاستفادة منها',
      point2Body: 'سترى أنماطك المهنية وملخصاً شخصياً ومسارات مقترحة، ويحسبها باكيلو على الخادم فقط.',
      point3Title: 'إرشاد عند الحاجة',
      point3Body: 'اطلب استشارة مهنية، وتابع الإشعارات، وتواصل مع الدعم من مكان واحد.',
      cta: 'ابدأ برقم جوالك',
      legalHint: 'يمكنك مراجعة الشروط وسياسة الخصوصية الحالية قبل إنشاء الحساب.',
      featuresTitle: 'ماذا يقدم باكيلو',
      howTitle: 'كيف يعمل التقييم',
      howBody: 'سجّل الدخول، وأجب عن الأسئلة المنشورة وفق وتيرتك، ثم راجع نتيجة يحسبها الخادم ويمكنك مناقشتها مع مختص.',
      consultTitle: 'هل تحتاج حواراً مع مختص؟',
      consultBody: 'بعد وجود نتيجة مكتملة يمكنك طلب استشارة مهنية من حسابك. يعيّن باكيلو المختص المهني المناسب بعد المراجعة. لا يتضمن هذا الموقع أي رسوم للاستشارات.',
    },
    auth: {
      title: 'سجّل الدخول أو أنشئ حساباً',
      subtitle: 'سنرسل رمزاً لمرة واحدة إلى رقم جوالك السعودي.',
      phone: 'رقم الجوال',
      phoneHint: 'استخدم رقماً سعودياً مثل 05XXXXXXXX.',
      channel: 'قناة الإرسال',
      sms: 'رسالة نصية',
      whatsapp: 'واتساب',
      sendCode: 'إرسال الرمز',
      verifyTitle: 'أدخل رمز التحقق',
      verifySubtitle: 'أرسلنا رمزاً مكوناً من 6 أرقام إلى {phone}.',
      code: 'رمز التحقق',
      expiresIn: 'ينتهي الرمز خلال {seconds} ث',
      resendIn: 'يمكنك إعادة الإرسال خلال {seconds} ث',
      remainingAttempts: 'تبقى {count} محاولات',
      codeExpired: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً.',
      invalidCode: 'رمز التحقق غير صحيح.',
      changePhone: 'استخدام رقم آخر',
      sessionExpired: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
      bootstrapping: 'جارٍ استعادة جلستك…',
      temporarilyUnavailable: 'باكيلو غير متاح مؤقتاً.',
      temporarilyUnavailableBody: 'تسجيل الدخول ما زال محفوظاً على هذا الجهاز. حاول مرة أخرى دون تسجيل الدخول من جديد.',
      storageUnavailable: 'تعذر على هذا المتصفح حفظ تسجيل الدخول. اسمح ببيانات الموقع لباكيلو ثم حاول مرة أخرى.',
      originMismatch: 'استخدم {origin} لتطوير باكيلو محلياً. هذا العنوان أصل متصفح مختلف.',
      fullName: 'الاسم الكامل',
      fullNameHint: 'مطلوب للحسابات الجديدة في باكيلو.',
      newAccount: 'بيانات الحساب الجديد',
      acceptTerms: 'قرأت شروط الاستخدام وأوافق عليها',
      acceptPrivacy: 'قرأت سياسة الخصوصية وأوافق عليها',
      codeNoLongerValid: 'انتهت صلاحية رمز التحقق أو لم يعد صالحاً. اطلب رمزاً جديداً للمتابعة.',
      requestNewCode: 'طلب رمز جديد',
      otpDeliveryUnavailable: 'إرسال رمز التحقق غير متاح مؤقتاً. ابق في هذه الصفحة وحاول مرة أخرى عندما تختار.',
    },
    profileComplete: {
      title: 'أكمل ملفك الشخصي',
      body: 'أضف بياناتك المهنية لنخصص تجربتك في باكيلو.',
      submit: 'حفظ ومتابعة',
      legalLoading: 'جارٍ تحميل المستندات المطلوبة…',
      legalFailed: 'تعذر تحميل المستندات النظامية المطلوبة. حاول مرة أخرى قبل المتابعة.',
    },
    legal: {
      acceptanceTitle: 'المراجعة والموافقة',
      acceptanceBody: 'يلزم قبول المستند النظامي الحالي قبل المتابعة.',
      version: 'الإصدار {version}',
      effective: 'ساري من {date}',
      missing: 'لا يتوفر مستند منشور حالياً.',
      terms: 'شروط الاستخدام',
      privacy: 'سياسة الخصوصية',
      acceptAll: 'الموافقة والمتابعة',
      required: 'الموافقة مطلوبة لاستخدام باكيلو.',
      read: 'اقرأ',
      explicitHint: 'اقرأ كل مستند ثم ضع علامة الموافقة. لا يسجّل باكيلو الموافقة تلقائياً أثناء تسجيل الدخول.',
    },
    home: {
      greeting: 'مرحباً بعودتك، {name}',
      greetingGuest: 'مرحباً بك في باكيلو',
      assessmentReady: 'تقييمك المهني جاهز للبدء.',
      assessmentUnavailable: 'لا يتوفر تقييم منشور حالياً.',
      inProgress: 'لديك تقييم قيد التنفيذ ({answered} من {total} تم الإجابة عليها).',
      latestResult: 'آخر نتيجة',
      noResult: 'أكمل التقييم وستظهر هنا شخصيتك المهنية.',
      consultation: 'الاستشارة المهنية',
      noConsultation: 'يمكنك طلب استشارة بعد وجود نتيجة مكتملة.',
      unread: '{count} إشعارات غير مقروءة',
    },
    assessment: {
      introTitle: 'تقييم الشخصية المهنية',
      introBody: 'يستكشف هذا التقييم أسلوبك في العمل. أجب بصدق؛ لا توجد إجابات صحيحة أو خاطئة.',
      howTitle: 'كيف يعمل التقييم',
      howBody: 'تُعرض الأسئلة في مجموعات صغيرة وبالترتيب الذي يعيده لارافيل. يمكنك الرجوع لمراجعة إجابات سابقة، ويتم حفظ إجاباتك أثناء التقدم.',
      privacyTitle: 'نتيجتك',
      privacyBody: 'يحسب باكيلو أنماطك المهنية على الخادم. لا يحسب الموقع النتائج ولا يعيد ترتيب المحتوى الرسمي.',
      section: 'القسم {current} من {total}',
      progress: 'السؤال {current} من {total}',
      questionRange: 'الأسئلة {start}–{end} من {total}',
      groupProgress: 'المجموعة {current} من {total}',
      invalid: 'تعذر تحميل هذا التقييم لأن قائمة الأسئلة غير صالحة.',
      chooseAnswer: 'اختر إجابة واحدة',
      saveFailed: 'لم يتم حفظ إجابتك. أعد المحاولة قبل المتابعة.',
      submitTitle: 'إرسال التقييم؟',
      submitBody: 'لن تتمكن من تعديل هذه الإجابات بعد الإرسال. سيجهّز باكيلو نتيجتك.',
      submitting: 'جارٍ إرسال تقييمك…',
      empty: 'لا توجد أسئلة نشطة في هذا التقييم بعد.',
      unavailable: 'لا يتوفر تقييم منشور.',
      completed: 'هذه المحاولة مكتملة مسبقاً.',
      stale: 'هذه المحاولة لم تعد صالحة. ابدأ التقييم الحالي أو استكمله.',
      generating: 'جارٍ تجهيز نتيجتك.',
      questionCount: '{count} أسئلة نشطة',
      sectionCount: '{count} أقسام',
      personalTitle: 'تقييمك الشخصي',
      personalBody: 'ابدأ أو استكمل تقييم باكيلو المهني الخاص بك. هذا مسار منفصل عن رمز التقييم المقدم من شركة.',
      sponsoredBadge: 'تقييم مقدم من {companyName}',
      companyCode: {
        title: 'هل لديك رمز تقييم من شركة؟',
        description: 'أدخل الرمز الذي زودتك به الشركة لبدء تقييم ممول من الشركة.',
        codeLabel: 'رمز التقييم',
        validate: 'التحقق من الرمز',
        companyLabel: 'مقدم من شركة {companyName}',
        startAttempt: 'استخدام الرمز وبدء التقييم',
        resumeAttempt: 'استخدام الرمز واستكمال التقييم',
        sponsorExplain: 'هذا تقييم برعاية الشركة المذكورة أعلاه. بعد إكمالك، ستتلقى الشركة اسمك الكامل ونتيجة التقييم الموجهة للمستخدم بالكامل.',
        consent: 'أوافق على مشاركة اسمي الكامل ونتيجة التقييم المهنية الكاملة مع الشركة الراعية المذكورة أعلاه.',
        startHint: 'سيبدأ هذا الرمز محاولة تقييم جديدة برعاية الشركة.',
        resumeHint: 'لقد استخدمت هذا الرمز مسبقاً. ستستكمل محاولتك الحالية.',
        expiresAt: 'صالح حتى {date}',
        validating: 'جاري التحقق من الرمز…',
        validated: 'تم التحقق من الرمز لـ {company}.',
        redeeming: 'جاري بدء التقييم…',
        cancel: 'مسح وإدخال رمز آخر',
        unavailable: 'رمز التقييم هذا غير متاح.',
        inProgressConflict: 'لديك تقييم قيد التقدم لا يمكن استبداله. أكمله أو استكمله قبل استخدام هذا الرمز.',
      },
    },
    results: {
      title: 'نتيجتك المهنية',
      historyTitle: 'سجل النتائج',
      latest: 'الأحدث',
      profileCode: 'رمز الملف',
      topPattern: 'النمط الأعلى',
      tied: 'تعادل',
      patterns: 'الأنماط المهنية',
      personalSummary: 'الملخص الشخصي',
      empty: 'لا توجد نتيجة مكتملة بعد.',
      snapshot: 'لقطة تاريخية',
      deletedItem: 'هذا العنصر لم يعد موجوداً في الكتالوج الحالي. تُعرض اللقطة المحفوظة.',
      sponsoredBy: 'برعاية {companyName}',
      types: {
        key_strength: 'قوة أساسية',
        professional_growth_challenge_opportunity: 'فرصة للنمو المهني',
        suggested_career_path: 'مسار مهني مقترح',
        skill_worth_developing: 'مهارة تستحق التطوير',
      },
      advice: 'نصيحة',
    },
    consultations: {
      title: 'الاستشارات المهنية',
      newTitle: 'طلب استشارة',
      empty: 'لم تطلب استشارة بعد.',
      needsResult: 'أكمل تقييماً أولاً. طلب الاستشارة يحتاج نتيجة مكتملة.',
      activeHint: 'لديك استشارة قيد المعالجة. سيعيّن باكيلو مختصاً بعد المراجعة.',
      assignHint: 'يعيّن باكيلو المختص المهني المناسب بعد مراجعة طلبك. لا تختار مختصاً من هنا.',
      goal: 'هدف الاستشارة',
      question: 'سؤالك أو ملاحظاتك',
      period: 'الفترة المفضلة',
      method: 'طريقة التواصل',
      methodVideo: 'فيديو',
      attempt: 'نتيجة التقييم المرتبطة',
      consent: 'أوافق على مشاركة تقريري مع المختص المعيّن',
      consentHint: 'يمكنك سحب صلاحية الاطلاع المستقبلية لاحقاً، وستبقى الاستشارة قائمة.',
      consentYes: 'مشاركة التقرير مع المختص المعيّن',
      consentNo: 'عدم مشاركة التقرير',
      review: 'راجع طلبك',
      success: 'تم إرسال طلب الاستشارة',
      reference: 'المرجع {reference}',
      referenceLabel: 'المرجع',
      specialist: 'المختص المعيّن',
      specialistPhoto: 'صورة {name}',
      specialistPhotoFallback: 'صورة المختص المهني',
      specialistUnavailable: 'الأخصائي لم يعد متاحًا',
      scheduled: 'الموعد',
      meeting: 'رابط الاجتماع',
      timezone: 'المنطقة الزمنية',
      reason: 'السبب',
      cancelTitle: 'إلغاء هذه الاستشارة؟',
      cancelBody: 'يمكنك الإلغاء فقط بينما يكون الطلب قيد المراجعة.',
      withdrawTitle: 'سحب موافقة مشاركة التقرير؟',
      withdrawBody: 'لن يحصل المختص على صلاحية مستقبلية للاطلاع على تقريرك. لن تُحذف الاستشارة.',
      noPayment: 'لا تتضمن استشارات باكيلو في هذا الموقع أي دفع أو تسعير.',
      statuses: {
        pending_review: 'قيد المراجعة',
        matched: 'تم التعيين',
        scheduled: 'مجدولة',
        completed: 'مكتملة',
        cancelled: 'ملغاة',
        rejected: 'مرفوضة',
      },
    },
    notifications: {
      title: 'الإشعارات',
      empty: 'لا توجد إشعارات بعد.',
      unread: 'غير مقروء',
      read: 'مقروء',
    },
    support: {
      title: 'الدعم',
      newTitle: 'تذكرة دعم جديدة',
      categories: 'كيف يمكننا مساعدتك؟',
      tickets: 'تذاكرك',
      emptyCategories: 'لا توجد فئات دعم متاحة حالياً.',
      emptyTickets: 'لم ترسل تذكرة دعم بعد.',
      category: 'الفئة',
      message: 'الرسالة',
      counter: '{count} / {max}',
      success: 'تم إرسال التذكرة',
      reference: 'المرجع {reference}',
      response: 'رد الإدارة',
      noResponse: 'لا يوجد رد بعد.',
      statuses: {
        open: 'مفتوحة',
        in_review: 'قيد المراجعة',
        resolved: 'تم الحل',
        closed: 'مغلقة',
      },
    },
    profile: {
      title: 'الملف والإعدادات',
      details: 'بيانات الملف',
      name: 'الاسم الكامل',
      fullName: 'الاسم الكامل',
      phone: 'رقم الجوال',
      locale: 'اللغة المفضلة',
      ageRange: 'الفئة العمرية',
      educationLevel: 'المستوى التعليمي',
      isEmployed: 'هل تعمل حالياً؟',
      yes: 'نعم',
      no: 'لا',
      workExperience: 'سنوات الخبرة',
      experienceField: 'مجال الخبرة',
      skills: 'المهارات',
      skillsHint: 'أضف مهارة ثم اضغط Enter أو فاصلة. يتم تجاهل التكرار.',
      skillsAdd: 'إضافة مهارة',
      skillsPlaceholder: 'اكتب مهارة',
      removeSkill: 'إزالة {skill}',
      skillAdded: 'تمت إضافة {skill}',
      skillRemoved: 'تمت إزالة {skill}',
      country: 'الدولة',
      selectPlaceholder: 'اختر خياراً',
      optionsLoading: 'جارٍ تحميل خيارات الملف…',
      optionsFailed: 'تعذر تحميل خيارات الملف. حاول مرة أخرى قبل المتابعة.',
      required: 'هذا الحقل مطلوب.',
      invalid: 'هذه القيمة غير صالحة.',
      saved: 'تم حفظ ملفك الشخصي.',
      links: 'روابط الحساب',
      danger: 'إجراءات خطرة',
    },
    deletion: {
      title: 'حذف حسابك',
      body: 'يؤرشف هذا الإجراء حساب باكيلو ويُخفيه. قد تبقى السجلات التشغيلية مثل المحاولات والاستشارات وتذاكر الدعم مرتبطة بمعرّف مجهول الهوية. هذا لا يعني محواً مادياً كاملاً.',
      confirmExplain: 'اطلب رمز التحقق، ثم اكتب الكلمة الإنجليزية DELETE حرفياً كما يطلبها الخادم. لا تُترجم هذه الكلمة لأنها جزء من عقد واجهة باكيلو.',
      request: 'إرسال رمز الحذف',
      destination: 'سيتم إرسال الرمز إلى رقم جوالك المسجّل.',
      expires: 'ينتهي الرمز خلال {seconds} ث',
      code: 'رمز التحقق',
      confirmation: 'اكتب DELETE للتأكيد',
      confirmationValue: 'DELETE',
      success: 'تم حذف حسابك.',
    },
    errors: {
      unexpected: 'حدث خطأ غير متوقع. حاول مرة أخرى.',
      network: 'يبدو أنك غير متصل أو أن الخدمة غير متاحة.',
      throttled: 'محاولات كثيرة. انتظر ثم حاول مرة أخرى.',
      throttledWait: 'محاولات كثيرة. حاول مرة أخرى خلال {seconds} ث.',
      validation: 'يرجى مراجعة الحقول المحددة.',
      forbidden: 'لا يمكنك الوصول إلى هذه الصفحة.',
      notFound: 'تعذر العثور على هذه الصفحة.',
      conflict: 'هذا الإجراء غير متاح في الحالة الحالية.',
      server: 'باكيلو غير متاح مؤقتاً. حاول لاحقاً.',
      envelope: 'أعاد الخادم استجابة غير متوقعة.',
    },
    empty: {
      generic: 'لا يوجد محتوى للعرض بعد.',
    },
    status: {
      loading: 'جارٍ التحميل',
    },
    confirm: {
      title: 'يرجى التأكيد',
    },
    notFound: {
      title: 'الصفحة غير موجودة',
      body: 'الصفحة التي تبحث عنها غير موجودة.',
      home: 'العودة إلى باكيلو',
    },
    forbidden: {
      title: 'الوصول مقيد',
      body: 'لا يمكن لهذا الحساب استخدام باكيلو حالياً.',
    },
    conflict: {
      title: 'هذا الإجراء غير متاح',
      body: 'الحالة الحالية لا تسمح بهذا الطلب. حدّث الصفحة ثم استخدم الخطوة الصحيحة.',
    },
    throttled: {
      title: 'يرجى الانتظار',
      body: 'تمت محاولات كثيرة. انتظر قليلاً ثم حاول مرة أخرى.',
    },
    company: {
      nav: {
        label: 'تنقل بوابة الشركة',
        dashboard: 'لوحة التحكم',
        plans: 'الباقات',
        purchases: 'المشتريات',
        credits: 'الأرصدة',
        codes: 'الرموز',
        participants: 'المشاركون',
        settings: 'الإعدادات',
      },
      plans: {
        kicker: 'للمنظمات',
        title: 'باقات تقييم الشركات',
        body: 'اشترِ أرصدة تقييم لفريقك، وأنشئ رموز وصول آمنة، واطلع على نتائج المشاركين بعد الإكمال.',
        empty: 'لا تتوفر باقات شركات حالياً.',
        featured: 'مميزة',
        credits: '{count} تقييمات',
        validity: 'صالحة لمدة {days} يوماً بعد التفعيل',
        view: 'عرض الباقة',
        detailsTitle: 'تفاصيل الباقة',
        terms: 'شروط الباقة',
        companyExplain: 'بعد تأكيد الدفع من باكيلو، تحصل شركتك على أرصدة لإنشاء رموز تقييم لمرة واحدة للمشاركين.',
        purchase: 'شراء الباقة',
      },
      onboarding: {
        kicker: 'إعداد الشركة',
        title: 'أنشئ ملف شركتك',
        body: 'سجّل منظمتك قبل شراء الباقات أو إدارة صلاحيات الفريق.',
        legalName: 'الاسم القانوني للشركة',
        displayName: 'الاسم المعروض',
        displayNameHint: 'يظهر للمشاركين عند استخدام الرمز.',
        registrationNumber: 'رقم السجل',
        contactEmail: 'البريد الإلكتروني للتواصل',
        contactPhone: 'هاتف التواصل',
        contactAddress: 'عنوان التواصل',
        submit: 'إنشاء الشركة',
      },
      dashboard: {
        kicker: 'بوابة الشركة',
        body: 'تابع الأرصدة والرموز والتقييمات المكتملة.',
        activePlans: 'الباقات النشطة',
        totalCredits: 'إجمالي الأرصدة',
        availableCredits: 'الأرصدة المتاحة',
        activeCodes: 'الرموز النشطة',
        claimedCodes: 'الرموز المستخدمة',
        completedAssessments: 'التقييمات المكتملة',
        recentParticipants: 'أحدث المشاركين',
      },
      purchases: {
        title: 'سجل المشتريات',
        body: 'راجع مشتريات الباقات وحالة الدفع.',
        empty: 'لا توجد مشتريات بعد.',
        buyPlan: 'تصفح الباقات',
      },
      purchase: {
        processing: 'جارٍ بدء الدفع…',
        amount: 'المبلغ',
        credits: 'التقييمات المشمولة',
        created: 'تاريخ الإنشاء',
        paidAt: 'تاريخ الدفع',
        reference: 'مرجع الدفع',
        plan: 'الباقة',
        pendingTitle: 'الدفع قيد الانتظار',
        pendingBody: 'شراؤك بانتظار تأكيد الدفع. لا تظهر الأرصدة إلا بعد تأكيد باكيلو للدفع.',
        checkStatus: 'التحقق من حالة الدفع',
        viewDetails: 'عرض تفاصيل الشراء',
        goToCodes: 'إنشاء الرموز',
        backToPurchases: 'العودة إلى المشتريات',
        statuses: {
          pending_payment: 'بانتظار الدفع',
          paid: 'مدفوع',
          failed: 'فشل',
          cancelled: 'ملغى',
          refunded: 'مسترد',
        },
        statusPage: {
          pendingTitle: 'الدفع ما زال قيد الانتظار',
          pendingBody: 'نتحقق من حالة الشراء على الخادم. لا تتوفر الأرصدة حتى يتم تأكيد الدفع.',
          successTitle: 'تم تأكيد الدفع',
          successBody: 'أصبح شراؤك نشطاً. يمكنك الآن إنشاء رموز التقييم.',
          failedTitle: 'لم يكتمل الدفع',
          failedBody: 'فشل هذا الشراء قبل تأكيد الدفع. ابدأ شراءً جديداً إذا كنت ما زلت تحتاج أرصدة.',
          cancelledTitle: 'تم إلغاء الشراء',
          cancelledBody: 'تم إلغاء هذا الشراء قبل تأكيد الدفع.',
          refundedTitle: 'تم استرداد الشراء',
          refundedBody: 'تم استرداد هذا الشراء. لم تعد أرصدة هذا الشراء متاحة.',
        },
      },
      credits: {
        title: 'الأرصدة',
        body: 'تأتي أرصدة الشركة من خادم باكيلو وتتحدث بعد المشتريات وإنشاء الرموز والاسترداد.',
        total: 'إجمالي الأرصدة',
        available: 'الأرصدة المتاحة',
        reserved: 'الأرصدة المحجوزة',
        consumed: 'الأرصدة المستهلكة',
        entitlements: 'الاستحقاقات النشطة',
        empty: 'لا توجد استحقاقات نشطة بعد.',
        availableCount: '{count} متاح',
      },
      codes: {
        title: 'رموز التقييم',
        body: 'أنشئ رموزاً لمرة واحدة للمشاركين. شاركها بشكل آمن.',
        generate: 'إنشاء رموز',
        generateTitle: 'إنشاء رموز تقييم',
        generatedTitle: 'تم إنشاء الرموز',
        entitlement: 'الاستحقاق',
        quantity: 'عدد الرموز',
        batchLabel: 'تسمية الدفعة (اختياري)',
        remainingCredits: '{count} رصيد متاح لهذا الاستحقاق',
        search: 'ابحث في الرموز أو المشاركين',
        allStatuses: 'كل الحالات',
        empty: 'لا توجد رموز بعد.',
        copy: 'نسخ',
        copyAll: 'نسخ الكل',
        copySuccess: 'تم النسخ',
        downloadCsv: 'تنزيل CSV',
        securityWarning: 'شارك هذه الرموز بأمان. أي شخص يملك الرمز يمكنه بدء تقييم برعاية الشركة.',
        revoke: 'إلغاء',
        revokeTitle: 'إلغاء رمز غير مستخدم؟',
        revokeBody: 'لن يعود بإمكان أحد استخدام هذا الرمز. يعيد الخادم الرصيد فقط عند السماح بذلك.',
        revokeConfirm: 'إلغاء الرمز',
        statuses: {
          active: 'نشط',
          claimed: 'مستخدم',
          completed: 'مكتمل',
          revoked: 'ملغى',
          expired: 'منتهٍ',
        },
      },
      participants: {
        title: 'المشاركون',
        body: 'المشاركون الذين استخدموا رمز الشركة. تظهر النتائج بعد الإكمال فقط.',
        empty: 'لا يوجد مشاركون بعد.',
      },
      participantResult: {
        badge: 'نتيجة مشارك برعاية الشركة',
        participant: 'المشارك: {name}',
      },
      settings: {
        title: 'إعدادات الشركة',
        body: 'حدّث بيانات الشركة وراجع الأعضاء المصرح لهم.',
        members: 'الأعضاء المصرح لهم',
        saved: 'تم تحديث ملف الشركة.',
      },
      codeEntry: {
        kicker: 'تقييم برعاية شركة',
        title: 'استخدم رمز تقييم الشركة',
        body: 'أدخل الرمز الذي وفرته منظمتك. لا يُخزَّن الرمز في عنوان المتصفح.',
        codeLabel: 'رمز التقييم',
        codeHint: 'مثل PACILO-XXXX-XXXX-XXXX',
        validate: 'متابعة',
        sponsorExplain: 'ستحصل الشركة الراعية على اسمك الكامل ونتيجة التقييم الموجهة للمستخدم بعد إكمالك.',
        consentFallback: 'أوافق على أن تحصل الشركة الراعية على اسمي الكامل ونتيجة التقييم الكاملة.',
        redeem: 'استخدام الرمز والبدء',
      },
    },
  },
};

export function translate(locale, key, vars) {
  const table = messages[locale] || messages.en;
  const value = lookup(table, key) ?? lookup(messages.en, key) ?? key;
  return interpolate(value, vars);
}
